import { toPercentStyle } from './bbox.js';
import { createZoomController } from './zoom.js';

// Per-word manuscript image panel: given an address (whatever the host app
// uses to identify a word -- a bare baseIdx, or a {perek, halakha, baseIdx}
// triple), show the witness page image with that word boxed and centred.
//
// The host supplies an `adapter`; the core owns all DOM, zoom/pan and state.
//
//   adapter.listWitnesses(address) -> [{ id, label }]
//       Witnesses that have imagery for this address. Return [] or omit the
//       method entirely for a single-witness panel (no tab strip); in that
//       case resolve() is called with witnessId === null.
//
// `showFolioLabel` may be a boolean or a function, for hosts where it depends
// on which work is currently loaded.
//
//   adapter.resolve(witnessId, address) -> {
//       status: 'exact' | 'approx' | 'no-data' | 'no-chapter' | 'not-found',
//       page:  { url, width, height },   // required for exact/approx
//       bbox:  [x, y, w, h] | null,      // null => show the page, no box
//       folio: string | number,          // optional, for the folio label
//       witnessLabel: string,            // optional, used in messages
//   }
//   See resolve.js#nearestEntry for the shared 'approx' fallback.

export const DEFAULT_LABELS = {
    noSelection: 'לחצו על מילה בטקסט כדי להציג את כתב היד',
    noData: (w) => `אין נתוני כתב יד עבור ${w}`,
    noChapter: (w) => `אין עדיין נתוני כתב יד לקטע זה ב${w}`,
    notFound: (w) => `המילה לא זוהתה ב${w} במיקום זה`,
    noImage: 'אין תצלום כתב יד למילה זו.',
    approxNote: 'המילה המדויקת לא זוהתה בכתב היד — מוצגת המילה המזוהה הקרובה ביותר',
    folio: (f) => 'דף ' + f,
    thisWitness: 'עד זה',
    zoomIn: 'הגדל',
    zoomOut: 'הקטן',
    reset: 'איפוס תצוגה',
    fullscreen: 'תצוגת מסך מלא',
    exitFullscreen: 'צא ממסך מלא',
};

export function createManuscriptPanel(options) {
    const {
        root,
        adapter,
        showFolioLabel = false,
        showFullscreen = true,
        zoom: zoomOpts,
        onWitnessChange = null,
    } = options;

    if (!root) throw new Error('createManuscriptPanel: `root` is required');
    if (!adapter || typeof adapter.resolve !== 'function') {
        throw new Error('createManuscriptPanel: `adapter.resolve` is required');
    }
    const labels = { ...DEFAULT_LABELS, ...(options.labels || {}) };

    // --- persistent chrome (built once; only the page image is re-rendered) ---
    root.classList.add('mvc-ms');
    root.innerHTML = '';

    const bar = el('div', 'mvc-ms-bar');
    const tabs = el('div', 'mvc-ms-tabs');
    const controls = el('div', 'mvc-ms-controls');
    const folioLabel = el('span', 'mvc-ms-folio');
    bar.append(tabs, folioLabel, controls);

    const viewport = el('div', 'mvc-ms-viewport');
    // The approximate-match caption sits OUTSIDE the scrolling viewport: the
    // panel opens zoomed in on the word, so anything appended after the image
    // starts scrolled out of view -- i.e. exactly the caveat the reader needs
    // would be the part they never see.
    const note = el('div', 'mvc-ms-note');
    note.hidden = true;
    root.append(bar, viewport, note);

    const zoomCtl = createZoomController(viewport, zoomOpts);

    const btnOut = button('−', labels.zoomOut, () => zoomCtl.zoomOut());
    const btnReset = button('⟲', labels.reset, () => zoomCtl.reset());
    const btnIn = button('+', labels.zoomIn, () => zoomCtl.zoomIn());
    controls.append(btnOut, btnReset, btnIn);

    let btnFull = null;
    if (showFullscreen && viewport.requestFullscreen) {
        btnFull = button('⛶', labels.fullscreen, () => zoomCtl.toggleFullscreen());
        controls.append(btnFull);
    }
    function onFullscreenChange() {
        if (!btnFull) return;
        const isFs = zoomCtl.isFullscreen();
        btnFull.title = isFs ? labels.exitFullscreen : labels.fullscreen;
        btnFull.classList.toggle('active', isFs);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);

    // --- state ---
    let address = null;
    let activeWitnessId = null;
    let currentImg = null;
    let imgLoadHandler = null;

    function setControlsEnabled(on) {
        [btnOut, btnReset, btnIn, btnFull].forEach(b => { if (b) b.disabled = !on; });
    }

    function placeholder(text) {
        viewport.innerHTML = '';
        viewport.append(Object.assign(el('div', 'mvc-ms-placeholder'), { textContent: text }));
        zoomCtl.detach();
        setControlsEnabled(false);
        folioLabel.textContent = '';
        note.hidden = true;
    }

    function renderTabs(witnesses) {
        tabs.innerHTML = '';
        if (witnesses.length < 2) { tabs.hidden = true; return; }
        tabs.hidden = false;
        witnesses.forEach(w => {
            const btn = el('button', 'mvc-ms-tab' + (w.id === activeWitnessId ? ' active' : ''));
            btn.type = 'button';
            btn.textContent = w.label || w.id;
            btn.addEventListener('click', () => {
                if (w.id === activeWitnessId) return;
                activeWitnessId = w.id;
                if (onWitnessChange) onWitnessChange(w.id);
                render();
            });
            tabs.appendChild(btn);
        });
    }

    function render() {
        // Drop any pending load handler from a previous render, so a slow
        // image that resolves after the user has clicked on doesn't yank the
        // scroll position to a word that is no longer selected.
        if (currentImg && imgLoadHandler) currentImg.removeEventListener('load', imgLoadHandler);
        currentImg = null; imgLoadHandler = null;

        if (address == null) {
            renderTabs([]);
            placeholder(labels.noSelection);
            return;
        }

        const witnesses = adapter.listWitnesses ? (adapter.listWitnesses(address) || []) : [];
        if (witnesses.length && !witnesses.some(w => w.id === activeWitnessId)) {
            activeWitnessId = witnesses[0].id;
        }
        renderTabs(witnesses);

        if (adapter.listWitnesses && !witnesses.length) {
            placeholder(labels.noImage);
            return;
        }

        const res = adapter.resolve(activeWitnessId, address) || { status: 'no-data' };
        const witnessLabel = res.witnessLabel
            || (witnesses.find(w => w.id === activeWitnessId) || {}).label
            || labels.thisWitness;

        if (res.status === 'no-data') return placeholder(labels.noData(witnessLabel));
        if (res.status === 'no-chapter') return placeholder(labels.noChapter(witnessLabel));
        if (res.status === 'not-found' || !res.page || !res.page.url) {
            return placeholder(labels.notFound(witnessLabel));
        }

        viewport.innerHTML = '';
        setControlsEnabled(true);
        const wantFolio = typeof showFolioLabel === 'function' ? showFolioLabel() : showFolioLabel;
        folioLabel.textContent = (wantFolio && res.folio != null) ? labels.folio(res.folio) : '';

        const wrap = el('div', 'mvc-ms-wrap');
        const img = el('img', 'mvc-ms-img');
        img.src = res.page.url;
        img.alt = witnessLabel + (res.folio != null ? `, ${res.folio}` : '');
        img.draggable = false;
        wrap.appendChild(img);

        let box = null;
        const pos = toPercentStyle(res.bbox, res.page);
        if (pos) {
            box = el('div', 'mvc-ms-box' + (res.status === 'approx' ? ' mvc-ms-box-approx' : ''));
            Object.assign(box.style, pos);
            wrap.appendChild(box);
        }
        viewport.appendChild(wrap);

        note.hidden = res.status !== 'approx';
        note.textContent = res.status === 'approx' ? labels.approxNote : '';

        zoomCtl.attach(wrap, box);

        // Centring must wait for the image's `load` event, not just the next
        // frame: until it loads, the wrap has no real height (nothing else
        // gives it one), so measuring/centring any earlier works against a
        // collapsed container and drifts once the image's layout lands.
        const focus = () => requestAnimationFrame(() => {
            if (box) zoomCtl.focus(); else zoomCtl.reset();
        });
        if (img.complete) {
            focus();
        } else {
            currentImg = img;
            imgLoadHandler = () => focus();
            img.addEventListener('load', imgLoadHandler, { once: true });
        }
    }

    render();

    return {
        // Show the word at `addr`. Pass null to return to the empty state.
        // `opts.witnessId` picks the witness in the same pass, so a click that
        // implies both ("this word, in that witness") is one render, not two.
        select(addr, opts) {
            address = addr;
            if (opts && opts.witnessId) activeWitnessId = opts.witnessId;
            render();
        },
        // Force a particular witness tab (e.g. the reader clicked that
        // witness's row in the synopsis, not just any word).
        selectWitness(id) { activeWitnessId = id; render(); },
        get witnessId() { return activeWitnessId; },
        get address() { return address; },
        refresh: render,
        destroy() {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            zoomCtl.destroy();
            root.innerHTML = '';
            root.classList.remove('mvc-ms');
        },
    };
}

function el(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
}

function button(text, title, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'mvc-ms-btn';
    b.textContent = text;
    b.title = title;
    b.addEventListener('click', onClick);
    return b;
}
